import {
  BROWSER_UPLOAD_PART_BYTES,
  BrowserUploadError,
} from './browser-multipart-upload';

/**
 * Largest file the composer sends as one PUT. The upload proxy refuses bodies
 * over 100 MB, so anything bigger goes in 64 MiB multipart parts instead.
 */
export const COMPOSER_SINGLE_PUT_MAX_BYTES = BROWSER_UPLOAD_PART_BYTES;
/** The composer's upload ceiling; the workspace quota can refuse sooner. */
export const COMPOSER_UPLOAD_MAX_BYTES = 2 * 1024 * 1024 * 1024;

export type ComposerUploadRoute = 'put' | 'multipart';

export function composerUploadRoute(sizeBytes: number): ComposerUploadRoute {
  return sizeBytes > COMPOSER_SINGLE_PUT_MAX_BYTES ? 'multipart' : 'put';
}

/** A file over the ceiling: refused before any network work. */
export class ComposerUploadTooLargeError extends Error {
  constructor() {
    super('COMPOSER_UPLOAD_TOO_LARGE');
    this.name = 'ComposerUploadTooLargeError';
  }
}

/**
 * Why an upload failed, as far as Retry is concerned. `too-large` and
 * `storage-full` fail the same way on every retry, so the composer offers
 * Remove only; everything else is worth retrying.
 */
export type ComposerUploadFailureReason =
  | 'too-large'
  | 'storage-full'
  | 'retryable';

function errorText(error: unknown): string {
  const data =
    error && typeof error === 'object' && 'data' in error ? error.data : '';
  return `${typeof data === 'string' ? data : ''} ${
    error instanceof Error ? error.message : String(error)
  }`;
}

export function composerUploadFailureReason(
  error: unknown
): ComposerUploadFailureReason {
  if (error instanceof ComposerUploadTooLargeError) return 'too-large';
  if (
    error instanceof BrowserUploadError &&
    error.code === 'WORKSPACE_STORAGE_LIMIT_EXCEEDED'
  )
    return 'storage-full';
  const text = errorText(error);
  // Workspace, member and team quotas (WORKSPACE_*STORAGE_LIMIT_EXCEEDED).
  if (/WORKSPACE_(?:[A-Z]+_)?STORAGE_LIMIT_EXCEEDED/.test(text))
    return 'storage-full';
  if (
    text.includes('COMPOSER_UPLOAD_TOO_LARGE') ||
    text.includes('UPLOAD_HTTP_413')
  )
    return 'too-large';
  return 'retryable';
}

/** A short, bounded failure code for analytics (no file names or URLs). */
export function composerUploadFailureCode(error: unknown): string {
  if (error instanceof BrowserUploadError) return error.code;
  const match = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/.exec(errorText(error));
  return match ? match[0].slice(0, 80) : 'UNKNOWN';
}

/** For a retryable failure, the cause the composer can name (#4809). */
export type ComposerUploadFailureCause = 'network' | 'server';

export function composerUploadFailureCause(
  error: unknown
): ComposerUploadFailureCause | undefined {
  if (composerUploadFailureReason(error) !== 'retryable') return undefined;
  if (typeof navigator !== 'undefined' && navigator.onLine === false)
    return 'network';
  if (
    error instanceof BrowserUploadError &&
    (error.code === 'UPLOAD_PART_FAILED' || error.code === 'UPLOAD_ABORTED')
  )
    return 'network';
  if (
    error instanceof BrowserUploadError &&
    error.code === 'UPLOAD_SESSION_FAILED'
  ) {
    // "<operation> <status> …" when the server answered; just the operation
    // when the request never got through (#4847 Live QA, offline mid-upload).
    const status = /\b(\d{3})\b/.exec(error.message)?.[1];
    if (!status) return error.message.endsWith('reply') ? 'server' : 'network';
    return status.startsWith('5') ? 'server' : undefined;
  }
  const text = errorText(error);
  if (
    /UPLOAD_HTTP_5\d\d|BROWSER_UPLOAD_|UPLOAD_REGISTRATION|ConvexError/.test(
      text
    )
  )
    return 'server';
  if (
    error instanceof TypeError ||
    /failed to fetch|networkerror|load failed|network|timed out|upload failed|UPLOAD_HTTP_0\b/i.test(
      text
    )
  )
    return 'network';
  return undefined;
}
